import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import SydAutocomplete from '../../../../commonDesign/SydAutocomplete'
import { useListViewTags } from '../../../../../api/abundanceEngine'
import useDebouncedValue from '../../../../../hooks/useDebounceValue'

/**
 * Per-view tag picker for the AE view create/edit form. Controlled sibling of
 * `ViewTagsAutocomplete.jsx` on the list page.
 *
 * `initialTags` exists to resolve saved selections synchronously — without it
 * we'd render a placeholder option, which SydAutocomplete would latch into
 * its internal selection state and refuse to refresh once the real label
 * arrives.
 */
function ViewTagsPicker ({ value = [], onChange, initialTags, placeholder = 'Select tags...' }) {
  const [searchText, setSearchText] = useState('')
  const debouncedText = useDebouncedValue(searchText, 200)

  const { data, isLoading } = useListViewTags({
    sort: [{ field: 'name', dir: 'asc' }],
    limit: 100,
    filters: debouncedText
      ? { search: [{ op: 'contains', value: debouncedText }] }
      : undefined
  })

  const allTags = useMemo(() => data || [], [data])

  const options = useMemo(
    () => allTags.map((tag) => ({
      label: tag.displayName || tag.name,
      subLabel: tag.description,
      value: tag.viewTagId
    })),
    [allTags]
  )

  // Cache selected-tag metadata so chips keep their labels when the user
  // searches and `allTags` filters down beneath the selection.
  const [selectedCache, setSelectedCache] = useState(() => new Map())
  useEffect(() => {
    const desiredIds = new Set(Array.isArray(value) ? value : [])
    setSelectedCache((prev) => {
      let next = prev
      const ensureCopy = () => {
        if (next === prev) next = new Map(prev)
      }
      for (const t of allTags) {
        if (t?.viewTagId != null && desiredIds.has(t.viewTagId) && prev.get(t.viewTagId) !== t) {
          ensureCopy()
          next.set(t.viewTagId, t)
        }
      }
      for (const cachedId of prev.keys()) {
        if (!desiredIds.has(cachedId)) {
          ensureCopy()
          next.delete(cachedId)
        }
      }
      return next
    })
  }, [allTags, value])

  const initialTagById = useMemo(() => {
    const map = new Map()
    for (const t of initialTags || []) {
      if (t?.viewTagId != null) map.set(t.viewTagId, t)
    }
    return map
  }, [initialTags])

  const selectedOptions = useMemo(() => {
    const ids = Array.isArray(value) ? value : []
    return ids
      .map((id) => {
        const tag = selectedCache.get(id) || initialTagById.get(id)
        if (!tag) return null
        return { label: tag.displayName || tag.name, subLabel: tag.description, value: tag.viewTagId }
      })
      .filter(Boolean)
  }, [value, selectedCache, initialTagById])

  return (
    <SydAutocomplete
      options={options}
      value={selectedOptions}
      onChange={(_e, selected) => {
        // Capture metadata for newly-selected ids in the same render that
        // updates `value`, so the next render's selectedOptions can resolve
        // them without a chip-disappearing flicker.
        setSelectedCache((prev) => {
          let next = prev
          for (const opt of (selected || [])) {
            if (opt?.value != null && !prev.has(opt.value)) {
              const tag = allTags.find((t) => t.viewTagId === opt.value)
              if (tag) {
                if (next === prev) next = new Map(prev)
                next.set(opt.value, tag)
              }
            }
          }
          return next
        })
        const ids = (selected || []).map((opt) => opt.value).filter((v) => v != null)
        onChange?.(ids)
      }}
      onInputChange={(_e, text) => setSearchText(text)}
      placeholder={placeholder}
      size='sm'
      isLoading={isLoading}
      multiple
    />
  )
}

ViewTagsPicker.propTypes = {
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func,
  initialTags: PropTypes.arrayOf(PropTypes.shape({
    viewTagId: PropTypes.number,
    name: PropTypes.string,
    displayName: PropTypes.string,
    description: PropTypes.string
  })),
  placeholder: PropTypes.string
}

export default ViewTagsPicker
